// add-payment.component.ts (DROP-IN: Square Web Payments SDK)
// Notes:
// - Removes CardInputComponent + angular-cc-library usage (no PAN/CVC in your app)
// - "Add" mode: mounts Square secure card UI, tokenizes, sends token to backend
// - "Edit" mode: only updates metadata (first/last name, primary). You can't edit card number/CVC.

import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subject, takeUntil } from "rxjs";
import {
  PaymentCardService,
  PaymentCard,
} from "../../shared/services/payment-card.service";
import { environment } from "../../../environments/environment";

declare global {
  interface Window {
    Square?: any;
  }
}

interface Validation {
  FirstName: boolean | null;
  LastName: boolean | null;
  SquareCardReady: boolean | null; // replaces cardNumber/expiry/cvc validations
}

interface DialogData {
  card?: PaymentCard | null;
  mode?: "add" | "edit";
}

@Component({
  selector: "app-add-payment",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./add-payment.component.html",
  styleUrl: "./add-payment.component.scss",
})
export class AddPaymentComponent implements OnInit, OnDestroy {
  // Must exist in HTML: <div id="square-card-container" #squareCardContainer></div>
  @ViewChild("squareCardContainer")
  squareCardContainer!: ElementRef<HTMLDivElement>;

  mode: "add" | "edit" = "add";
  cardId: number | null = null;

  // Optional display summary for edit mode (used in the HTML I gave you)
  existingCardSummary = "";

  isSubmitting = false;
  errorMessage = "";
  private destroy$ = new Subject<void>();

  formData = {
    firstName: "",
    lastName: "",
    isPrimary: false,
  };

  validations: Validation = {
    FirstName: true,
    LastName: true,
    SquareCardReady: true,
  };

  // Square SDK instances
  private payments: any | null = null;
  private card: any | null = null;

  // Pull these from env.json/runtime config in your app (recommended)
  // Sandbox SDK: https://sandbox.web.squarecdn.com/v1/square.js
  private squareAppId = environment.squareAppId;
  private squareLocationId = "SANDBOX_LOCATION_ID";
  private useSandbox = true;

  constructor(
    private dialogRef: MatDialogRef<AddPaymentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private paymentCardService: PaymentCardService,
  ) {}

  async ngOnInit(): Promise<void> {
    // Determine mode
    if (this.data?.card) {
      this.mode = "edit";
      this.cardId = this.data.card.id || null;

      // Populate only metadata fields (no card number / cvc)
      this.formData = {
        firstName: this.data.card.first_name || "",
        lastName: this.data.card.last_name || "",
        isPrimary: !!this.data.card.is_primary,
      };

      // Optional: build summary string if your PaymentCard includes these
      // (You should store these from Square: brand/last4/exp_month/exp_year)
      const brand =
        (this.data.card as any).brand ||
        (this.data.card as any).card_brand ||
        "";
      const last4 =
        (this.data.card as any).last4 || (this.data.card as any).last_4 || "";
      const expMonth = (this.data.card as any).exp_month || "";
      const expYear = (this.data.card as any).exp_year || "";

      if (brand || last4) {
        const exp =
          expMonth && expYear
            ? ` · exp ${String(expMonth).padStart(2, "0")}/${String(expYear).slice(-2)}`
            : "";
        this.existingCardSummary = `${brand || "Card"} •••• ${last4}${exp}`;
      } else {
        this.existingCardSummary = "Saved card on file";
      }

      return; // do not initialize Square card UI in edit mode
    }

    // Add mode: init Square
    try {
      await this.ensureSquareSdkLoaded();
      await this.initSquareCard();
      this.validations.SquareCardReady = true;
    } catch (e: any) {
      this.validations.SquareCardReady = false;
      this.errorMessage = e?.message || "Failed to initialize payment form.";
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.card = null;
    this.payments = null;
  }

  onClose(): void {
    this.dialogRef.close({ success: false });
  }

  private validateMeta(): boolean {
    const fn = (this.formData.firstName || "").trim();
    const ln = (this.formData.lastName || "").trim();

    const firstOk = !!fn;
    const lastOk = !!ln;

    this.validations.FirstName = firstOk;
    this.validations.LastName = lastOk;

    return firstOk && lastOk;
  }

  async handleSubmit(): Promise<void> {
    this.errorMessage = "";

    if (!this.validateMeta()) {
      this.errorMessage = "Please fix the validation errors before submitting.";
      return;
    }

    // EDIT MODE: update only metadata (primary/name)
    if (this.mode === "edit" && this.cardId) {
      this.isSubmitting = true;

      this.paymentCardService
        .updateCardMeta(this.cardId, {
          first_name: this.formData.firstName,
          last_name: this.formData.lastName,
          is_primary: this.formData.isPrimary,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: PaymentCard) => {
            this.isSubmitting = false;
            this.dialogRef.close({ success: true, card: response });
          },
          error: (error: any) => {
            this.isSubmitting = false;
            this.errorMessage =
              error?.message ||
              "Failed to update payment card. Please try again.";
          },
        });

      return;
    }

    // ADD MODE: tokenize via Square and send token to backend
    if (!this.card) {
      this.validations.SquareCardReady = false;
      this.errorMessage = "Payment form is not ready yet.";
      return;
    }

    this.isSubmitting = true;

    try {
      const tokenizeResult = await this.card.tokenize();

      if (tokenizeResult.status !== "OK") {
        const msg =
          tokenizeResult?.errors?.[0]?.message || "Card tokenization failed.";
        throw new Error(msg);
      }

      const token: string = tokenizeResult.token;

      // Send token to backend (backend will create customer if missing and save card on file)
      this.paymentCardService
        .saveCardWithSquareToken({
          token,
          first_name: this.formData.firstName,
          last_name: this.formData.lastName,
          is_primary: this.formData.isPrimary,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: PaymentCard) => {
            this.isSubmitting = false;
            this.dialogRef.close({ success: true, card: response });
          },
          error: (error: any) => {
            this.isSubmitting = false;
            this.errorMessage =
              error?.message || "Failed to add payment card. Please try again.";
          },
        });
    } catch (e: any) {
      this.isSubmitting = false;
      this.errorMessage = e?.message || "Failed to add payment card.";
    }
  }

  // -----------------------
  // Square SDK helpers
  // -----------------------

  private async ensureSquareSdkLoaded(): Promise<void> {
    if (window.Square) return;

    const src = this.useSandbox
      ? "https://sandbox.web.squarecdn.com/v1/square.js"
      : "https://web.squarecdn.com/v1/square.js";

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Square SDK failed to load."));
      document.head.appendChild(script);
    });
  }

  private async initSquareCard(): Promise<void> {
    if (!window.Square) throw new Error("Square SDK not available.");

    if (!this.squareAppId || !this.squareLocationId) {
      throw new Error("Missing Square appId/locationId configuration.");
    }

    this.payments = window.Square.payments(
      this.squareAppId,
      this.squareLocationId,
    );
    this.card = await this.payments.card();

    // Attach to the container in your template
    // <div id="square-card-container" #squareCardContainer></div>
    await this.card.attach("#square-card-container");
  }
}
